import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAdminPaymentsApi,
  getPendingPaymentsApi,
  approvePaymentApi,
  rejectPaymentApi,
} from "../../api/adminPaymentsApi";

// Initial State
const initialState = {
  payments: [],
  totalPayments: 0,
  loading: false,
  error: null,
  success: false,
  pendingPayments: [],
  pendingPaymentsCount: 0,
  pendingLoading: false,
  filters: {
    status: null,
    restaurantId: null,
    invoiceId: null,
    paymentMethod: null,
    startDate: null,
    endDate: null,
    search: null,
  },
  pagination: {
    page: 1,
    limit: 10,
    totalPages: 0,
  },
};

// ✅ Get All Payments
export const fetchAdminPayments = createAsyncThunk(
  "adminPayments/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await getAdminPaymentsApi(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch payments"
      );
    }
  }
);

// ✅ Get Pending Payments
export const fetchPendingPayments = createAsyncThunk(
  "adminPayments/fetchPending",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getPendingPaymentsApi();
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch pending payments"
      );
    }
  }
);

// ✅ Approve Payment
export const approvePayment = createAsyncThunk(
  "adminPayments/approve",
  async (id, { rejectWithValue }) => {
    try {
      const response = await approvePaymentApi(id);
      return { id, data: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to approve payment"
      );
    }
  }
);

// ✅ Reject Payment
export const rejectPayment = createAsyncThunk(
  "adminPayments/reject",
  async (id, { rejectWithValue }) => {
    try {
      const response = await rejectPaymentApi(id);
      return { id, data: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to reject payment"
      );
    }
  }
);

// Slice
const adminPaymentSlice = createSlice({
  name: "adminPayments",
  initialState,
  reducers: {
    clearPayments: (state) => {
      state.payments = [];
      state.totalPayments = 0;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.page = 1;
    },
    clearFilters: (state) => {
      state.filters = {
        status: null,
        restaurantId: null,
        invoiceId: null,
        paymentMethod: null,
        startDate: null,
        endDate: null,
        search: null,
      };
      state.pagination.page = 1;
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    clearError: (state) => {
      state.error = null;
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch All Payments
      .addCase(fetchAdminPayments.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(fetchAdminPayments.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.payments = action.payload?.payments || [];
        state.totalPayments = action.payload?.totalPayments || 0;
        state.pagination.totalPages = action.payload?.totalPages || 0;
        state.pagination.page = action.payload?.currentPage || 1;
        state.pagination.limit = action.payload?.limit || 10;
      })
      .addCase(fetchAdminPayments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Something went wrong";
        state.success = false;
        state.payments = [];
        state.totalPayments = 0;
        state.pagination.totalPages = 0;
      })

      // Fetch Pending Payments
      .addCase(fetchPendingPayments.pending, (state) => {
        state.pendingLoading = true;
        state.error = null;
      })
      .addCase(fetchPendingPayments.fulfilled, (state, action) => {
        state.pendingLoading = false;
        state.pendingPayments = action.payload?.payments || [];
        state.pendingPaymentsCount = action.payload?.count || 0;
      })
      .addCase(fetchPendingPayments.rejected, (state, action) => {
        state.pendingLoading = false;
        state.error = action.payload || "Failed to fetch pending payments";
        state.pendingPayments = [];
        state.pendingPaymentsCount = 0;
      })

      // Approve Payment
      .addCase(approvePayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(approvePayment.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        // Update payment status in the list
        const index = state.payments.findIndex(
          (p) => p._id === action.payload.id
        );
        if (index !== -1) {
          state.payments[index].status = "APPROVED";
        }
        // Remove from pending payments if present
        state.pendingPayments = state.pendingPayments.filter(
          (p) => p._id !== action.payload.id
        );
        state.pendingPaymentsCount = state.pendingPayments.length;
      })
      .addCase(approvePayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to approve payment";
      })

      // Reject Payment
      .addCase(rejectPayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(rejectPayment.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        // Update payment status in the list
        const index = state.payments.findIndex(
          (p) => p._id === action.payload.id
        );
        if (index !== -1) {
          state.payments[index].status = "REJECTED";
        }
        // Remove from pending payments if present
        state.pendingPayments = state.pendingPayments.filter(
          (p) => p._id !== action.payload.id
        );
        state.pendingPaymentsCount = state.pendingPayments.length;
      })
      .addCase(rejectPayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to reject payment";
      });
  },
});

// Export Actions
export const {
  clearPayments,
  setFilters,
  clearFilters,
  setPagination,
  clearError,
  resetState,
} = adminPaymentSlice.actions;

// ============ SELECTORS ============
// ============ SELECTORS ============

// Basic Selectors
export const selectAllPayments = (state) => state.adminPayments.payments;
export const selectTotalPayments = (state) => state.adminPayments.totalPayments;
export const selectPaymentLoading = (state) => state.adminPayments.loading;
export const selectPaymentError = (state) => state.adminPayments.error;
export const selectPaymentSuccess = (state) => state.adminPayments.success;
export const selectFilters = (state) => state.adminPayments.filters;
export const selectPagination = (state) => state.adminPayments.pagination;

// Pending Payments Selectors
export const selectPendingPayments = (state) => state.adminPayments.pendingPayments;
export const selectPendingPaymentsCount = (state) => state.adminPayments.pendingPaymentsCount;
export const selectPendingLoading = (state) => state.adminPayments.pendingLoading;

// Filtered Selectors
export const selectPaymentsByStatus = (state, status) => {
  if (!status) return state.adminPayments.payments;
  return state.adminPayments.payments.filter(
    (payment) => payment.status === status
  );
};

export const selectApprovedPayments = (state) => {
  return state.adminPayments.payments.filter(
    (payment) => payment.status === "APPROVED"
  );
};

export const selectRejectedPayments = (state) => {
  return state.adminPayments.payments.filter(
    (payment) => payment.status === "REJECTED"
  );
};

// ✅ FIXED: Calculation Selectors - Get amount from invoiceId
export const selectTotalPaymentAmount = (state) => {
  return state.adminPayments.payments.reduce(
    (total, payment) => total + (payment.invoiceId?.amount || payment.amount || 0),
    0
  );
};

export const selectApprovedAmount = (state) => {
  return state.adminPayments.payments
    .filter((payment) => payment.status === "APPROVED")
    .reduce((total, payment) => total + (payment.invoiceId?.amount || payment.amount || 0), 0);
};

export const selectPendingAmount = (state) => {
  return state.adminPayments.payments
    .filter((payment) => payment.status === "PENDING")
    .reduce((total, payment) => total + (payment.invoiceId?.amount || payment.amount || 0), 0);
};

export default adminPaymentSlice.reducer;